import { Controller } from "@hotwired/stimulus"
import { warmUpMediaPlayback } from "../audio_alerts"
import {
  deliveryFailureIntro,
  DELIVERY_FAILED,
  markDeliveryFailed,
  markDeliveryVerified,
  osNotificationSteps,
  readDeliveryState,
  sitePermissionGranted
} from "../notification_delivery"
import {
  shouldNotifyTransition,
  transitionNotificationBody,
  transitionNotifyKey
} from "../transition_alerts"
import { alertContext, logTransition } from "../transition_debug"

export default class extends Controller {
  static targets = [
    "banner",
    "notificationsPrompt",
    "deniedPrompt",
    "unsupportedPrompt",
    "deliveryModal",
    "deliveryIntro",
    "deliverySteps"
  ]

  connect() {
    this.onTransition = this.onTransition.bind(this)
    this.onVisibilityChange = this.onVisibilityChange.bind(this)

    this.lastNotifiedKey = null
    this.activeNotification = null
    this.shownWhileHidden = false

    document.addEventListener("timer:transition", this.onTransition)
    document.addEventListener("visibilitychange", this.onVisibilityChange)

    logTransition("alerts controller connected", alertContext())

    this.updateBannerVisibility()

    if (readDeliveryState() === DELIVERY_FAILED && sitePermissionGranted()) {
      this.showDeliveryFailedModal()
    }
  }

  disconnect() {
    document.removeEventListener("timer:transition", this.onTransition)
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
    this.closeActiveNotification()
  }

  async onVisibilityChange() {
    if (document.visibilityState === "visible") {
      if (this.activeNotification && this.shownWhileHidden) {
        this.closeActiveNotification()
      }
    }

    this.updateBannerVisibility()
  }

  onTransition(event) {
    const detail = event.detail || {}
    const notificationsGranted = Notification.permission === "granted"
    const willNotify = shouldNotifyTransition({ notificationsGranted })

    if (!willNotify) {
      logTransition("browser notification skipped", {
        detail,
        reason: !this.notificationsSupported()
          ? "Notifications API not supported"
          : Notification.permission === "default"
            ? "permission not granted — click Enable notifications"
            : Notification.permission === "denied"
              ? "permission denied in browser settings"
              : "unknown"
      })
      return
    }

    logTransition("browser notification requested", { detail })
    this.notifyTransition(detail)
  }

  async enableNotifications() {
    if (!this.notificationsSupported() || !this.secureContext() || !this.canRequestPermission()) return

    try {
      await Notification.requestPermission()
      if (Notification.permission === "granted") {
        await warmUpMediaPlayback()
      }
    } catch (error) {
      console.error("Notification permission request failed:", error)
    }
    this.updateBannerVisibility()
  }

  dismissDeliveryModal() {
    this.closeDeliveryModal()
  }

  showDeliveryFailedModal() {
    this.renderDeliverySteps()

    if (this.hasDeliveryIntroTarget) {
      this.deliveryIntroTarget.textContent = deliveryFailureIntro()
    }

    if (this.hasDeliveryModalTarget) {
      this.deliveryModalTarget.showModal()
    }
  }

  closeDeliveryModal() {
    if (this.hasDeliveryModalTarget) {
      this.deliveryModalTarget.close()
    }
  }

  renderDeliverySteps() {
    if (!this.hasDeliveryStepsTarget) return

    this.deliveryStepsTarget.innerHTML = osNotificationSteps()
      .map((step) => `<li>${step}</li>`)
      .join("")
  }

  updateBannerVisibility() {
    if (!this.hasBannerTarget) return

    const supported = this.notificationsSupported()
    const secure = this.secureContext()
    const notificationPermission = supported ? Notification.permission : "unsupported"
    const canPromptNotifications =
      supported &&
      secure &&
      this.canRequestPermission() &&
      notificationPermission === "default"
    const notificationsDenied = supported && secure && notificationPermission === "denied"
    const notificationsUnsupported = !supported || !secure || !this.canRequestPermission()

    const showBanner = canPromptNotifications || notificationsDenied || notificationsUnsupported

    this.bannerTarget.classList.toggle("hidden", !showBanner)

    if (this.hasUnsupportedPromptTarget) {
      this.unsupportedPromptTarget.classList.toggle("hidden", !notificationsUnsupported)
    }

    if (this.hasNotificationsPromptTarget) {
      this.notificationsPromptTarget.classList.toggle(
        "hidden",
        !canPromptNotifications || notificationsDenied
      )
    }

    if (this.hasDeniedPromptTarget) {
      this.deniedPromptTarget.classList.toggle("hidden", !notificationsDenied)
    }
  }

  notifyTransition(detail) {
    if (!this.notificationsSupported()) {
      logTransition("browser notification aborted", { detail, reason: "not supported" })
      return
    }

    if (Notification.permission !== "granted") {
      logTransition("browser notification aborted", {
        detail,
        reason: `permission is ${Notification.permission}`
      })
      return
    }

    const notifyKey = transitionNotifyKey(detail)

    if (this.lastNotifiedKey === notifyKey) {
      logTransition("browser notification skipped", { detail, reason: "duplicate", notifyKey })
      return
    }

    this.lastNotifiedKey = notifyKey
    const body = transitionNotificationBody(detail)

    logTransition("creating Notification", { detail, body, tag: "block-transition" })

    this.showNotification({ body, tag: "block-transition" }).then((result) => {
      if (result.ok) {
        logTransition("browser notification delivered", { detail, body })
        markDeliveryVerified()
        this.closeDeliveryModal()
        return
      }

      logTransition("browser notification failed", { detail, reason: result.reason, error: result.error })
      this.lastNotifiedKey = null
      markDeliveryFailed()
      this.showDeliveryFailedModal()
    })
  }

  showNotification({ body, tag }) {
    return new Promise((resolve) => {
      let settled = false

      const settle = (result) => {
        if (settled) return
        settled = true
        resolve(result)
      }

      try {
        this.closeActiveNotification()

        const notification = new Notification("Posture Loop", {
          body,
          icon: "/icon-192.png",
          tag,
          renotify: true,
          silent: false
        })

        notification.onclick = (event) => {
          event.preventDefault()
          window.focus()
          notification.close()
        }

        notification.onshow = () => {
          this.activeNotification = notification
          this.shownWhileHidden = document.hidden
          logTransition("Notification.onshow fired", { body, tag })
          settle({ ok: true })
        }

        notification.onclose = () => {
          if (this.activeNotification === notification) {
            this.activeNotification = null
            this.shownWhileHidden = false
          }
        }

        notification.onerror = () => {
          logTransition("Notification.onerror fired", { body, tag })
          settle({ ok: false, reason: "error" })
        }

        this.activeNotification = notification

        // onshow is unreliable in background tabs; treat a successful constructor as delivery.
        queueMicrotask(() => {
          settle({ ok: true })
        })
      } catch (error) {
        logTransition("Notification constructor threw", { body, tag, error: String(error) })
        settle({ ok: false, reason: "throw", error })
      }
    })
  }

  closeActiveNotification() {
    if (this.activeNotification) {
      this.activeNotification.close()
      this.activeNotification = null
      this.shownWhileHidden = false
    }
  }

  notificationsSupported() {
    return "Notification" in window
  }

  canRequestPermission() {
    return this.notificationsSupported() && typeof Notification.requestPermission === "function"
  }

  secureContext() {
    return window.isSecureContext
  }

}
